import { addHours, isToday } from 'date-fns';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BundleBadge } from '~/components/BundleBadge';
import DeliveryDateLabel from '~/components/DeliveryDateLabel/DeliveryDateLabel';
import { MultibuyBadge } from '~/components/MultibuyBadge';
import { useProductCard } from '~/components/ProductCard/hooks/useProductCard';
import ProductCardV3 from '~/components/ProductCardV3';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { useNotifyMe } from '~/hooks/useNotifyMe';
import { mainStore, userStore } from '~/stores';
import type { Product, ProductState } from '~/stores/CategoriesStore';
import { formatPriceWithCurrency } from '~/utils/formaters';
import { getLowestMultibuyLevel } from '~/utils/multibuy';

import Badge from '../Badge/Badge';
import CloudflareResponseImage from '../CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '../CloudflareResponsiveImage/constants';
import Counter from '../Counter';
import DiscountBadge from '../DiscountBadge/DiscountBadge';
import ExpressDeliveryBadge from '../ExpressDeliveryBadge/ExpressDeliveryBadge';
import Icon from '../Icon/Icon';
import { PropVariants } from '../PropVariants/PropVariants';
import TooltipSelector from '../SmallSelector/SmallSelector';
import styles from './ProductCard.module.scss';
import { formatVolume, pluralizePcLabel } from './utils';

interface ProductCardProps {
  product: Product;
  className?: string;
  isRatingShown?: boolean;
  hideButton?: boolean;
  productState?: ProductState;
}

const _ProductCard = ({
  product,
  className,
  isRatingShown = true,
  hideButton = false,
  productState,
}: ProductCardProps) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const { isMobile } = useGlobal();
  const {
    currentItem,
    offerSku,
    volumes,
    limit,
    image,
    leadTime,
    oldPrice,
    currentPrice,
    cartItemsCount,
    isMultiBuy,
    isExpress,
    isBackorderAvailable,
    isBundle,
    isFavorite,
    productHasManyVariants,
    colorVariants,
    selectOffer,
    toggleFavorite,
    changeCount,
    addMultiBuyProductToCart,
    openSelectSKUModal,
  } = useProductCard(
    productState?.data ?? product,
    productState?.selectedOffer,
  );

  const { notifyMeOnClick, notifyMeIcon, notifyMeButtonText, btnClassName } =
    useNotifyMe(
      productState?.data?.id ?? product?.id ?? 0,
      productState?.selectedOffer?.sku ?? currentItem.sku ?? '',
      isBundle,
    );

  // TODO - rating
  // const rating = useMemo(() => {
  //   return product.ratingAverage ? (
  //     <div className="product-card__rated">
  //       <Icon type="star-fill" size={12} />
  //       {product.ratingAverage}
  //     </div>
  //   ) : (
  //     <div className={styles.no_rating}>
  //       <Icon type="star-fill" size={12} className={styles.no_rating__star} />
  //       {t('noRatingShort')}
  //     </div>
  //   );
  // }, [product.ratingAverage]);

  const addButtonHandler = () => {
    productHasManyVariants && !isBundle
      ? openSelectSKUModal()
      : changeCount(cartItemsCount + 1, 'add');
  };

  const ctaButtonComponent =
    limit > 0 ? (
      cartItemsCount > 0 ? (
        <Counter
          count={cartItemsCount}
          size={isMobile ? 's' : 'm'}
          onClick={changeCount}
          limit={limit}
        />
      ) : (
        <button
          data-link={`pruduct-add-to-card-${product?.slug}`}
          className={classes([
            'button _primary product-card__buy',
            isMobile ? '_xs' : '_s',
          ])}
          onClick={addButtonHandler}
        >
          <Icon type="bag" size={isMobile ? 16 : 24} />
          {t('addToCart')}
        </button>
      )
    ) : (
      <button
        data-link={`pruduct-add-to-card-${product?.slug}`}
        className={classes([
          'button _bordered product-card__buy',
          isMobile ? '_xs' : '_s',
          btnClassName,
        ])}
        onClick={notifyMeOnClick}
      >
        <Icon type={notifyMeIcon} size={isMobile ? 16 : 24} />
        {notifyMeButtonText}
      </button>
    );

  const deliveryBadgeComponent = (() => {
    if (limit === 0) {
      return (
        <Badge theme="gray" size="s">
          {t('comingSoon')}
        </Badge>
      );
    }
    if (isExpress) {
      return <ExpressDeliveryBadge />;
    }
    if (isBackorderAvailable) {
      return (
        <DeliveryDateLabel
          deliveryDate={
            isToday(addHours(new Date(), leadTime))
              ? null
              : addHours(new Date(), leadTime)
          }
        />
      );
    }
  })();

  return (
    <div className={classes(['product-card', className])}>
      <div
        className={classes([
          'product-card__img-wrap',
          isMultiBuy && styles.multibuyImageWr,
        ])}
      >
        <CloudflareResponseImage
          src={currentItem.images[0] ?? image ?? ''}
          srcParams={{ width: 210, height: 274 }}
          srcSetParams={defaultSrcSetParams}
          alt=""
          className="product-card__img"
          loading="lazy"
        />
        <Link
          data-link={`pruduct-${product?.slug}`}
          onClick={handleClickProduct(product)}
          to={`/p/${product?.slug}`}
          state={{ offerSku }}
          className="link-abs"
        />
        <div className={styles.infoBadges}>
          {deliveryBadgeComponent}
          {oldPrice && +currentPrice < +oldPrice && (
            <DiscountBadge
              currentPrice={+currentPrice}
              oldPrice={+oldPrice}
              size={'s'}
              theme="primary"
            />
          )}
        </div>
        {isBundle && <BundleBadge className={styles.bundleBadge} />}
        {isMultiBuy &&
          (() => {
            const lowestLevel = getLowestMultibuyLevel(
              currentItem.multibuyPriceLevels,
            );
            if (!lowestLevel) {
              return null;
            }
            return (
              <MultibuyBadge
                value={lowestLevel.quantity}
                className={styles.multibuyCounter}
                onClick={addMultiBuyProductToCart}
              />
            );
          })()}
        {userStore.isAuthorized && (
          <button
            data-button="favourite"
            className={classes([
              'button _no-padding product-card__fav',
              isFavorite && '_fill',
            ])}
            onClick={toggleFavorite}
          >
            <Icon type={isFavorite ? 'fav-fill' : 'fav'} size={20} />
          </button>
        )}
      </div>
      <div className="product-card__body">
        <div className="product-card__info">
          {/* TODO - rating */}
          <span />
          {/* {isRatingShown && rating} */}
          {!hideButton &&
            (isBundle ? (
              <p className="product-card__info-volume">{pluralizePcLabel(1)}</p>
            ) : (
              (volumes?.length > 1 && (
                <TooltipSelector
                  onChange={selectOffer}
                  options={volumes}
                  id={currentItem.sku || ''}
                  side={isRatingShown ? 'left' : 'right'}
                />
              )) || (
                <p className="product-card__info-volume">
                  {formatVolume(currentItem.properties)}
                </p>
              )
            ))}
        </div>
        {colorVariants.length > 0 && (
          <PropVariants colorVariants={colorVariants} />
        )}
        <div className="product-card__name">
          <Link
            data-link={`pruduct-name-${product?.slug}`}
            to={`/p/${product.slug}`}
            onClick={handleClickProduct(product)}
            state={{ offerSku }}
          >
            {currentItem.name}
          </Link>
        </div>
        <div className="product-card__footer">
          <div className="product-card__price-wrap">
            <div
              className={classes([
                'product-card__price',
                oldPrice && 'good',
                isMultiBuy && styles.multibuyOldPrice,
              ])}
            >
              {formatPriceWithCurrency(+currentPrice)}
              {isMultiBuy ? ' / 1x' : ''}
            </div>
            {oldPrice && (
              <div className="product-card__price _old">
                {formatPriceWithCurrency(+oldPrice)}
              </div>
            )}
          </div>
          {isMultiBuy &&
            (() => {
              const lowestLevel = getLowestMultibuyLevel(
                currentItem?.multibuyPriceLevels,
              );
              if (!lowestLevel) {
                return null;
              }
              return (
                <div className={styles.multibuyPriceWr}>
                  <div className={styles.multibuyFromRule}>
                    {t('phrases:fromPc', {
                      length: pluralizePcLabel(lowestLevel.quantity),
                    })}
                  </div>
                  <div className="product-card__price good">
                    {formatPriceWithCurrency(
                      mainStore.convertPenceToPounds(lowestLevel.unitPrice),
                    )}
                  </div>
                </div>
              );
            })()}
          {!hideButton && ctaButtonComponent}
        </div>
      </div>
    </div>
  );
};

export default observer(ProductCardV3);
